import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AssetSymbol, BlockType } from '@tradingblock/types';
import { BlockDragHandle, HttpAutocomplete, OptionType, getBlockTypeName, Pill } from '@tradingblock/components';
import { SymbolToOption, useSymbolSearch } from '../../hooks/useSymbolSearch';
import { FavoriteButton } from '../../components/blocks/FavoriteButton';
import NewsSubscriptionModal from './NewsSubscriptionModal';
import { InfoDataActions } from '../../data/global/actions/InfoActions';
import { useBlockId } from '../../components/blocks/BlockState';
import { SymbolChannels } from '../../types';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../data/global/dataSelectors';
import { useGroupAssetSymbol } from '../useGroupState';
import { useInfoSettings } from './hooks/useInfoSettings';
import _ from 'lodash';
import { NewsStatusIcon } from '../../components/dashboard/NewsStatus';

interface InfoSymbolPickerHeaderProps {
  blockType: BlockType;
  symbol: AssetSymbol | undefined;
  isLoaded: boolean | undefined;
  onSelect: (value: OptionType<AssetSymbol>) => void;
  onClear?: () => void;
  placeholder?: string;
  clearOptionsOnSelect?: boolean;
  focus?: boolean;
  disableFavorite?: boolean;
  hidePrice?: boolean;
  hideTime?: boolean;
  hidePercent?: boolean;
  onSearch?: (value: string, limit?: number | undefined) => void;
  onBlur?: () => void;
}

export const InfoSymbolPickerHeader: React.FunctionComponent<InfoSymbolPickerHeaderProps> = ({
  blockType,
  symbol,
  isLoaded,
  onSelect,
  placeholder,
  clearOptionsOnSelect,
  focus,
  onClear,
  disableFavorite,
  onSearch,
  onBlur,
}) => {
  const dispatch = useDispatch();
  const blockId = useBlockId();
  const newsBlock = useStateSelector(s => s.information.marketNews[blockId]);
  const newsFeedStatus = useStateSelector(s => s.newsFeedProvider.status);
  const symbolRequest = useMemo(() => {
    return newsBlock && newsBlock.symbolRequest;
  }, [newsBlock && newsBlock.symbolRequest]);
  const currentFilters = useMemo(() => {
    return newsBlock && newsBlock.filteredNewsSymbols;
  }, [newsBlock && newsBlock.filteredNewsSymbols]);

  const search = useSymbolSearch();
  const symbolValue = symbol ? symbol.symbol : '';
  const autoCompleteValue = useMemo(() => {
    return symbol ? SymbolToOption(symbol, true) : undefined;
  }, [symbol]);
  const groupAsset = useGroupAssetSymbol();
  const rest = useMemo(() => {
    return { autoFocus: focus };
  }, [focus]);

  const [showModal, setShowModal] = useState<boolean>(false);
  const settings = useInfoSettings();
  const elementRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const showCurrentFiltersScrolling = useMemo(() => {
    return currentFilters ? dimensions.width / 2 < currentFilters.length * 60 + 110 : false;
  }, [currentFilters, dimensions]);
  const showScrollingTicker = useMemo(() => {
    return symbolRequest ? dimensions.width / 2 < symbolRequest.length * 60 + 110 : false;
  }, [symbolRequest, dimensions]);

  useEffect(() => {
    const handleResize = entries => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions({ width, height });
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (elementRef.current) {
      resizeObserver.observe(elementRef.current);
    }

    // Cleanup observer on component unmount
    return () => resizeObserver.disconnect();
  }, []);

  const channelMappings = [
    { active: settings.dailyNewsfeed, name: 'DailyNewsfeed' },
    { active: settings.corporateActionsDividends, name: 'CorporateActionsDividends' },
    { active: settings.earningsReports, name: 'EarningsReports' },
    { active: settings.rumorsAndIdeas, name: 'RumorsAndIdeas' },
    { active: settings.companyNews, name: 'CompanyNews' },
    { active: settings.sectorNews, name: 'SectorNews' },
    { active: settings.ipos, name: 'Ipos' },
    { active: settings.economicsAndGovernment, name: 'EconomicsAndGovernment' },
    { active: settings.theMarkets, name: 'TheMarkets' },
    { active: settings.etfCoverage, name: 'EtfCoverage' },
    { active: settings.options, name: 'Options' },
  ];

  const channelArray: string[] = [];
  // Populate channelArray for MarketNews polling request based on useInfoSettings()
  if (_.isEmpty(settings)) {
    channelArray.push('DailyNewsfeed');
  } else {
    channelMappings.forEach(channelObj => {
      if (channelObj.active) channelArray.push(channelObj.name);
    });
  }

  const isDJIA = (groupAsset && groupAsset.symbol === '$DJIA') || (groupAsset && groupAsset.symbol === '$DJI');
  const isSPX = groupAsset && groupAsset.symbol === '$SPX';
  const isNasdaq = groupAsset && groupAsset.symbol.includes('$NDX');
  const isVIX = groupAsset && groupAsset.symbol === '$VIX';
  const isUnsupportedIndex = groupAsset && !isDJIA && !isSPX && !isNasdaq && !isVIX && groupAsset.symbol[0] === '$';

  useEffect(() => {
    if (symbol) {
      handleAddNews(symbol.symbol);
    } else {
      handleUpdateFilterNews([]);
    }
  }, [symbol]);

  const handleAddNews = useCallback(
    (symbol: string) => {
      if (!isUnsupportedIndex && symbolRequest && !symbolRequest.find(s => s.name === symbol)) {
        const addSymbol: SymbolChannels = {
          name: symbol,
          channels: channelArray,
        };

        dispatch(InfoDataActions.editMarketNewsBlock({ blockId: blockId, symbols: [...symbolRequest, addSymbol] }));
      }
      handleUpdateFilterNews([symbol]);
    },
    [symbolRequest]
  );

  function handleUpdateFilterNews(filter: string[] = []) {
    dispatch(InfoDataActions.filterMarketNewsBlock({ blockId: blockId, symbols: [...filter] }));
  }

  const handleRemoveNews = useCallback(
    (symbol: string) => {
      const remainingSymbols: SymbolChannels[] = symbolRequest.filter(s => s.name !== symbol);
      dispatch(InfoDataActions.editMarketNewsBlock({ blockId: blockId, symbols: [...remainingSymbols] }));
      handleUpdateFilterNews(currentFilters.filter(f => f !== symbol));
    },
    [symbolRequest, currentFilters]
  );

  return (
    <div className="block-nav nav" ref={elementRef}>
      <BlockDragHandle className="nav-item">{getBlockTypeName(blockType)}</BlockDragHandle>
      {(!newsFeedStatus || newsFeedStatus !== 'open') && (
        <div className="nav-item">
          <NewsStatusIcon />
        </div>
      )}
      {newsFeedStatus && newsFeedStatus === 'open' && (
        <>
          <div className="nav-item">
            <Pill>
              {isLoaded && (
                <HttpAutocomplete
                  onClear={onClear}
                  rest={rest}
                  minimumLength={1}
                  search={(value: string, limit?: number | undefined) => {
                    if (onSearch !== undefined) {
                      onSearch(value, limit);
                    }

                    return search(value, limit);
                  }}
                  onBlur={onBlur}
                  value={autoCompleteValue}
                  onSelect={onSelect}
                  clearOptionsOnSelect={clearOptionsOnSelect}
                  placeholder={placeholder}
                  symbol={symbolValue}
                  charLimit={20}
                />
              )}
            </Pill>
          </div>
          {symbol && (
            <>
              <div className="nav-item nav-stock-name">
                <div className="txt-sm">
                  <strong>{symbol.symbol}</strong>
                </div>
                <div className="txt-sm mute" title={symbol.name}>
                  {symbol.name}
                </div>
              </div>
              {!disableFavorite && (
                <div className="nav-item">
                  <FavoriteButton symbol={symbol} />
                </div>
              )}
            </>
          )}

          {symbolRequest && symbolRequest.length > 0 && (
            <div className="nav-item">
              <button
                title="Edit News Subscriptions"
                onClick={() => {
                  setShowModal(true);
                }}
              >
                <i className="fal fa-pencil"></i>
              </button>
            </div>
          )}

          {currentFilters && currentFilters.length > 0 && (
            <>
              {showCurrentFiltersScrolling && (
                <div className="nav-item-last">
                  <div className="scrolling-ticker">
                    <div className="ticker-content">
                      <strong>
                        Showing:{' '}
                        {[...currentFilters].map((s, i) => s + (i !== currentFilters.length - 1 ? ' |  ' : ''))}
                      </strong>
                    </div>
                  </div>
                </div>
              )}
              {!showCurrentFiltersScrolling && (
                <div className="nav-item-last">
                  <div className="txt-sm">
                    <strong>
                      Showing: {[...currentFilters].map((s, i) => s + (i !== currentFilters.length - 1 ? ' |  ' : ''))}
                    </strong>
                  </div>
                </div>
              )}
            </>
          )}
          {currentFilters && currentFilters.length === 0 && symbolRequest && symbolRequest.length > 0 && (
            <>
              {showScrollingTicker && (
                <div className="nav-item-last">
                  <div className="scrolling-ticker">
                    <div className="ticker-content">
                      <strong>
                        Showing:{' '}
                        {[...symbolRequest.map(sc => sc.name)].map(
                          (s, i) => s + (i !== symbolRequest.length - 1 ? ' |  ' : '')
                        )}
                      </strong>
                    </div>
                  </div>
                </div>
              )}
              {!showScrollingTicker && (
                <div className="nav-item-last">
                  <div className="txt-sm">
                    <strong>
                      Showing:{' '}
                      {[...symbolRequest.map(sc => sc.name)].map(
                        (s, i) => s + (i !== symbolRequest.length - 1 ? ' |  ' : '')
                      )}
                    </strong>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {showModal && <NewsSubscriptionModal show={showModal} setShow={setShowModal} removeNews={handleRemoveNews} />}
    </div>
  );
};
